import { Operation } from "@apollo/client";
import { NetworkError } from "@apollo/client/errors";
import * as Sentry from "@sentry/react";
import { isString, toLower, uniq, uniqBy } from "lodash";

import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";

export type ErrorMatchType = {
  match: string;
  title: string;
  description?: string;
};

export const findErrorMatch = (
  message: string,
  errorMatches: Array<ErrorMatchType>,
) =>
  errorMatches.find(({ match }) =>
    String(message).toLowerCase().includes(match.toLowerCase()),
  );

// The GraphQL errors that no errorMatch handles, one per message. Each one goes
// to Sentry: a field that fails on every item of a list is still one event.
export const unexpectedGraphqlErrors = <T extends { message: string }>(
  graphQLErrors: ReadonlyArray<T>,
  errorMatches: Array<ErrorMatchType>,
) =>
  uniqBy(
    graphQLErrors.filter(
      (graphQLError) => !findErrorMatch(graphQLError.message, errorMatches),
    ),
    "message",
  );

/**
 * The GraphQL messages hiding inside a failed *batched* response.
 *
 * graphene sets a batch's HTTP status to the highest status of its entries, so
 * one bad operation returns 400 for the whole batch. Apollo lifts `result.errors`
 * into `graphQLErrors` for a single response, but a batch's `result` is an array
 * of per-operation payloads, so nothing is lifted and the real cause is lost.
 *
 * The caller cannot tell which entry belongs to its operation, so these are
 * attached as Sentry context only, and never to the Error itself: that would
 * give every distinct batch its own Sentry issue (TF-1687).
 */
const batchedErrorMessages = (networkError: NetworkError): string[] => {
  if (!networkError || !("result" in networkError)) return [];

  const result = networkError.result;
  if (!Array.isArray(result)) return [];

  return uniq(
    result.flatMap((entry) =>
      Array.isArray(entry?.errors)
        ? entry.errors.map((error: { message?: string }) => error?.message)
        : [],
    ),
  ).filter(isString);
};

// The browser could not reach the server. Each browser words it differently:
// Chrome "Failed to fetch", Safari "Load failed", Firefox "NetworkError when
// attempting to fetch resource".
const OFFLINE_ERROR_MESSAGES = [
  "failed to fetch",
  "load failed",
  "networkerror when attempting to fetch resource",
];

export const isOfflineError = (networkError: NetworkError) => {
  // A response with a status code reached the server, so it is never offline.
  if (!networkError || "statusCode" in networkError) return false;
  const message = toLower(String(networkError));
  return OFFLINE_ERROR_MESSAGES.some((offline) => message.includes(offline));
};

/**
 * One Sentry issue per operation and status code.
 *
 * Every network error is created on the same line, so Sentry's default
 * grouping (by stack trace) put a 504 on one query and a dropped request on
 * another into one issue, under whichever title came first.
 */
export const networkErrorFingerprint = (
  networkError: NetworkError,
  operation?: Operation,
) => [
  "network-error",
  operation?.operationName || "unknown-operation",
  networkError && "statusCode" in networkError
    ? String(networkError.statusCode)
    : networkError?.name || "unknown-error",
];

export const defaultNetworkErrorHandler = (
  networkError: NetworkError,
  operation?: Operation,
  // Set when the caller renders this operation's error inline. It hides the
  // generic toast only. The session-expired, stale-CSRF and offline prompts are
  // conditions of the app, not of one operation, so they always show, and the
  // Sentry report always happens (TF-1687).
  { suppressGenericToast = false }: { suppressGenericToast?: boolean } = {},
) => {
  if (!networkError) return;

  if ("statusCode" in networkError && networkError.statusCode === 401) {
    const loginUrl = `/login?next=${window.location.pathname}`;
    errorNotificationVar({
      timeout: null,
      title: "Your session has expired.",
      description: (
        <div>
          Please refresh the page and{" "}
          <a href={loginUrl} className="underline text-blue-600">
            log in
          </a>
          .
          <div className="mt-4">
            <Button url={loginUrl} theme={buttonTheme.primary} text="Login" />
          </div>
        </div>
      ),
    });
  } else if (
    "statusCode" in networkError &&
    networkError.statusCode === 403 &&
    "result" in networkError &&
    isString(networkError.result) &&
    networkError.result.toLowerCase().includes("csrf")
  ) {
    errorNotificationVar({
      timeout: null,
      title: "This page is out of date",
      description: (
        <div>Please make note of any unsaved work and refresh to continue</div>
      ),
    });
  } else if (isOfflineError(networkError)) {
    errorNotificationVar({
      description: "Make sure you are connected to the internet and try again.",
    });
  } else {
    if (!suppressGenericToast) {
      errorNotificationVar({
        description: `An unexpected error occurred. We have been notified. Please try again.${
          window.Cypress ? JSON.stringify(networkError) : ""
        }`,
      });
    }
    const batched = batchedErrorMessages(networkError);
    Sentry.captureException(
      new Error(
        `[Network error] ${operation?.operationName || ""}: ${networkError}`,
      ),
      {
        fingerprint: networkErrorFingerprint(networkError, operation),
        extra: {
          operationName: operation?.operationName,
          variables: operation?.variables,
          error: networkError,
          ...(batched.length ? { batchedErrorMessages: batched } : {}),
        },
      },
    );
  }
};
